// src/pages/Checkout.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { getUsShippingUSD } from "../utils/pricing";
import { createCheckoutSession } from "../services/stripeService";
import Navigation from "../components/Common/header/Navigation";
import Footer from "../components/Common/footer/Footer";
import "../styles/Checkout.css";

const Checkout = () => {
    // Pulling the new context variables
    const { cart, cartSubtotal, cartTotal, discount, appliedCode, hasUnavailableItems } = useCart();
    const { currentUser } = useAuth();
    const { currency, formatPrice, settings } = useCurrency();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Shipping matches what the server charges: £4.99 UK standard, or the US fee set in the admin.
    // (The old £10 shipping + 8% tax lines didn't match what Stripe actually charged.)
    const shipping = currency === "USD" ? getUsShippingUSD(cartSubtotal, settings) : 4.99;
    const finalCheckoutTotal = cartTotal + shipping;

    useEffect(() => {
        if (cart.length === 0) {
            navigate("/shop");
        }
    }, [cart, navigate]);

    const handleCheckout = async () => {
        setLoading(true);
        setError("");

        try {
            const checkoutItems = [...cart];
            // Passing appliedCode to backend so Stripe applies it
            await createCheckoutSession(checkoutItems, currentUser?.email, currentUser?.uid || null, appliedCode, currency);
        } catch (err) {
            console.error("Checkout failed:", err);
            setError("Failed to initialize secure checkout. Please try again.");
            setLoading(false);
        }
    };

    if (cart.length === 0) return null;

    return (
        <div className="checkout-page">
            <Navigation />
            <div className="container py-5">
                <h2 className="checkout-page-title">Secure Checkout</h2>
                <div className="checkout-title-divider" />

                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <div className="summary-panel" style={{ marginTop: 0 }}>
                            <p className="summary-title text-center">Order Summary</p>

                            <div style={{ marginBottom: "20px" }}>
                                {cart.map(item => (
                                    <div key={item.id} className="summary-item">
                                        <span className="summary-item-name">
                                            {item.name} {item.variantLabel ? `(${item.variantLabel})` : item.length ? `(${item.length})` : ''} × {item.quantity}
                                        </span>
                                        <span style={item.unavailable ? { color: "#e57373" } : undefined}>
                                            {item.unavailable ? "Not available in US" : formatPrice(item.price * item.quantity)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <hr className="summary-divider" />
                            <div className="summary-item">
                                <span>Subtotal</span>
                                <span>{formatPrice(cartSubtotal)}</span>
                            </div>

                            {/* Show discount if applied */}
                            {discount > 0 && (
                                <div className="summary-item" style={{ color: "#d4af37" }}>
                                    <span>Discount ({appliedCode})</span>
                                    <span>-{formatPrice(discount)}</span>
                                </div>
                            )}

                            <div className="summary-item">
                                <span>{currency === "USD" ? "US Delivery" : "Standard Delivery"}</span>
                                <span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span>
                            </div>
                            <hr className="summary-divider" />
                            <div className="summary-total">
                                <span>Total</span>
                                <span>{formatPrice(finalCheckoutTotal)}</span>
                            </div>

                            <div className="terms-checkbox mt-4 mb-3 text-start">
                                <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={agreedToTerms}
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        style={{ marginTop: '5px', accentColor: '#d4af37', width: '16px', height: '16px', flexShrink: 0 }}
                                    />
                                    <span style={{ fontSize: '0.8rem', color: '#a0aec0', lineHeight: '1.6' }}>
                                        I have read and agree to the <Link to="/terms" style={{ color: '#d4af37' }} target="_blank">Terms & Conditions</Link> and <Link to="/privacy" style={{ color: '#d4af37' }} target="_blank">Privacy Policy</Link>, and I acknowledge the strict no-returns policy.
                                    </span>
                                </label>
                            </div>

                            {error && (
                                <div className="alert alert-danger mt-3" style={{ fontSize: "0.9rem" }}>
                                    {error}
                                </div>
                            )}

                            <button
                                className="checkout-btn w-100"
                                onClick={handleCheckout}
                                disabled={loading || !agreedToTerms || hasUnavailableItems}
                                style={{ opacity: ((!agreedToTerms || hasUnavailableItems) && !loading) ? 0.5 : 1 }}
                            >
                                {loading ? (
                                    <span><i className="fas fa-spinner fa-spin me-2"></i> Redirecting to Stripe...</span>
                                ) : (
                                    <span>Proceed to Secure Payment <i className="fas fa-lock ms-2"></i></span>
                                )}
                            </button>

                            <div className="text-center mt-3" style={{ color: "#a0aec0", fontSize: "0.85rem" }}>
                                <i className="fab fa-stripe fa-lg me-1"></i> Payments processed securely by Stripe
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default Checkout;